import Checkbox from '@material-ui/core/Checkbox';
import React, { useEffect, useState } from "react";
import "../../sass/main.scss";
import './FormField.scss';
import unknown_release from "../../assets/images/unknown_release.svg";
import dont_release from "../../assets/images/dont_release.svg";
import { SensitiveInfoRelease } from '../../models/transaction';


export interface Item<TId extends number = number> {
    readonly id: TId;
    readonly label: string;
    readonly state: SensitiveInfoRelease;
}

interface CheckboxListProps<TId extends number = number> {
    items: readonly Item<TId>[],
    onChange?: (updatedItemId: TId, checked: boolean) => void,
    checkboxHeader: string,
    labelHeaders: readonly string[],
    className?: string,
    allowUncheck?: boolean,
    style?: React.CSSProperties,
    hasFreeText?: (item: Item<TId>) => boolean,
    freeText?: string,
    freeTextMaxLen?: number
    onChangeFreeText?: (text: string) => void,
    /**
     * Lift the free text on every keystroke instead of only on blur. Needed
     * wherever the box can be destroyed without ever blurring - Quick Entry's
     * row collapses on Confirm, and a click straight from the box to Confirm
     * takes the un-lifted text with it (AB#39380, David Heifetz on PR !10721).
     * Off by default: classic mode's onChangeFreeText POSTs the release, so
     * per-keystroke there would be one write per character.
     */
    liftFreeTextOnChange?: boolean,
}

/**
 * Component for setting information release.
 * 
 * @param props 
 * @returns 
 */
export function CheckboxList<TId extends number = number>(props: CheckboxListProps<TId>) {
    const items = props.items;
    const [freeText, setFreeText] = useState(props.freeText ?? "");

    // Re-seed from the parent whenever the PARENT's value changes - a document replace, a purpose
    // reconciliation or a revert resets it, and this useState was seeded once and never resynced,
    // so stale text stayed in the box and the next keystroke lifted it straight back
    // (Mike Hamilton, PR !10721). With liftFreeTextOnChange the parent echoes every keystroke
    // back, so this DOES run mid-typing - it re-writes the string already in state, which React
    // discards, so it cannot fight the typist or move the caret. In blur-only mode the parent is
    // deliberately behind and it does not run until the box blurs.
    useEffect(() => { setFreeText(props.freeText ?? ""); }, [props.freeText]);

    const onChangeData = (checked: boolean, changedRow: Item<TId>) => {
        if (props.freeText && props.hasFreeText?.(changedRow) && !checked) {
            setFreeText("");
            props.onChangeFreeText?.("");
        }
        items.map(item => item.id == changedRow.id ? {
            ...item,
            state: checked ? SensitiveInfoRelease.Authorized : (props.allowUncheck ? SensitiveInfoRelease.NotMentioned : SensitiveInfoRelease.Prohibited),
        } : item);

        props.onChange?.(changedRow.id, checked);
    }

    const onBlurText = () => {
        if (freeText != (props.freeText ?? "")) {
            props.onChangeFreeText?.(freeText);
        }
    }

    const onChangeText = (text: string) => {
        setFreeText(text);
        if (props.liftFreeTextOnChange) {
            props.onChangeFreeText?.(text);
        }
    }

    return <table className={props.className} style={props.style}>
        <thead>
            <tr>
                <td>
                    {props.checkboxHeader}
                </td>
                {props.labelHeaders.map((h, i) => <td
                    key={`labelHeader${i}`}
                >
                    {h}
                </td>)}
                {props.hasFreeText && <td></td>}
            </tr>
        </thead>
        <tbody>
            {items.map((row, index) => <tr key={"row" + index}>
                <td>
                    <span>
                        <Checkbox
                            onChange={(e) => onChangeData(e.target.checked, row)}
                            checked={row.state == SensitiveInfoRelease.Authorized}
                            icon={<img src={row.state == SensitiveInfoRelease.Prohibited ? dont_release : unknown_release}/>}
                        />
                    </span>
                </td>
                <td>
                    {row.label}
                </td>
                {props.hasFreeText && <td>
                    {props.hasFreeText(row) && row.state == SensitiveInfoRelease.Authorized && <textarea
                        maxLength={props.freeTextMaxLen}
                        className="checkboxlist-text"
                        value={freeText}
                        onChange={e => onChangeText(e.currentTarget.value)}
                        onBlur={onBlurText}
                    />}
                </td>}
            </tr>)}
        </tbody>
    </table>
}

export default CheckboxList;

